import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose
} from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { PlayerDialogProps } from './types';
import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { fetchCoaches } from '@/services/coachService';

interface PlayerEvaluation {
  id?: string;
  coach_id: string;
  evaluation_id: string;
  player_id?: string;
  ratings?: { [key: string]: number };
  rating_average?: number;
  rating_count?: number;
  notes?: string;
  team_recommendations?: string[];
  updated_at?: string;
  [key: string]: any;
}

const PlayerDialog = ({
    isOpen,
    onOpenChange,
    currentPlayerId,
    currentPlayerNotes,
    setCurrentPlayerNotes,
    onSave,
    playerName,
    currentTeamId
}: PlayerDialogProps) => {
  // Fetch previously saved notes for this player/team when dialog opens
  useEffect(() => {
    async function fetchNotes() {
      if (!isOpen || !currentPlayerId || !currentTeamId) return;
      const { data, error } = await supabase
        .from('team_player_notes')
        .select('notes')
        .eq('player_id', currentPlayerId)
        .eq('team_id', currentTeamId)
        .single();
      if (!error && data && typeof data.notes === 'string') {
        setCurrentPlayerNotes(data.notes);
      } else {
        setCurrentPlayerNotes('');
      }
    }
    fetchNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentPlayerId, currentTeamId]);
  const [evaluations, setEvaluations] = useState<PlayerEvaluation[]>([]);
  const [loadingEvals, setLoadingEvals] = useState(false);
  const [evalError, setEvalError] = useState<string | null>(null);
  const [coachIdToName, setCoachIdToName] = useState<Record<string, string>>({});

  // Team offers state
  const [teamOffers, setTeamOffers] = useState<any[]>([]);
  const [loadingOffers, setLoadingOffers] = useState(false);
  const [offersError, setOffersError] = useState<string | null>(null);

  // Fetch team offers for this player
  useEffect(() => {
    async function fetchTeamOffers() {
      if (!currentPlayerId) return;
      setLoadingOffers(true);
      setOffersError(null);
      // Join team_players with teams to get team name
      const { data, error } = await supabase
        .from('team_players')
        .select('id, team_id, player_id, status, created_at, updated_at, teams (id, name, sex, description)')
        .eq('player_id', currentPlayerId);
      if (error) setOffersError(error.message);
      else setTeamOffers(data || []);
      setLoadingOffers(false);
    }
    if (isOpen) fetchTeamOffers();
  }, [isOpen, currentPlayerId]);

  // Fetch evaluations
  useEffect(() => {
    async function fetchEvaluations() {
      if (!currentPlayerId) return;
      setLoadingEvals(true);
      setEvalError(null);
      // Fetch all coach evaluations for this player
      const { data, error } = await supabase
        .from('player_evaluations')
        .select('*')
        .eq('player_id', currentPlayerId)
        .order('updated_at', { ascending: false });
      if (error) setEvalError(error.message);
      else setEvaluations((data as PlayerEvaluation[]) || []);
      setLoadingEvals(false);
    }
    if (isOpen) fetchEvaluations();
  }, [isOpen, currentPlayerId]);

  // Fetch coaches and build coachIdToName map after evaluations are loaded
  useEffect(() => {
    async function loadCoachNames() {
      if (!evaluations.length) return;
      const coaches = await fetchCoaches();
      const map: Record<string, string> = {};
      coaches.forEach(coach => {
        map[coach.id] = coach.name ?? coach.id;
      });
      setCoachIdToName(map);
    }
    loadCoachNames();
  }, [evaluations]);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Player Details{playerName ? `: ${playerName}` : ''}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <Textarea
            placeholder="Enter coach notes about this player for this team..."
            className="min-h-[200px]"
            value={currentPlayerNotes}
            onChange={(e) => setCurrentPlayerNotes(e.target.value)}
          />
        </div>
        <section className="mt-8">
          <h3 className="text-lg font-bold mb-4 border-b pb-1 text-gray-800">Coach Evaluations & Team Recommendations</h3>
          {loadingEvals ? (
            <div className="py-6 text-center text-gray-500">Loading evaluations...</div>
          ) : evalError ? (
            <div className="py-6 text-center text-red-500">{evalError}</div>
          ) : evaluations.length === 0 ? (
            <div className="py-6 text-center text-gray-400">No evaluations found for this player.</div>
          ) : (
            <div className="flex flex-col gap-6">
              {evaluations.map((evalItem, i) => (
                <div
                  key={evalItem.id || i}
                  className="rounded-xl border bg-white shadow-md px-6 py-4 flex flex-col gap-3 md:flex-row md:items-start md:gap-8"
                  style={{ borderLeft: '6px solid #6366f1' }}
                >
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="text-base font-semibold text-indigo-700">
                        {coachIdToName[evalItem.coach_id] || evalItem.coach_id}
                      </span>
                      <span className="text-xs text-gray-400">({evalItem.updated_at?.slice(0, 16).replace('T', ' ')})</span>
                    </div>
                    <div className="mb-2">
                      <span className="font-medium text-gray-700">Notes:</span>{' '}
                      {evalItem.notes ? (
                        <span className="text-gray-800">{evalItem.notes}</span>
                      ) : (
                        <span className="italic text-gray-400">No notes</span>
                      )}
                    </div>
                    <div className="mb-2">
                      <span className="font-medium text-gray-700">Ratings:</span>{' '}
                      {evalItem.ratings && Object.keys(evalItem.ratings).length > 0 ? (
                        <div className="flex flex-wrap gap-2 mt-1">
                          {Object.entries(evalItem.ratings).map(([category, value]) => (
                            <span
                              key={category}
                              className="inline-flex items-center px-2 py-1 rounded-full bg-indigo-100 text-indigo-800 text-xs font-semibold border border-indigo-200"
                            >
                              <span className="font-mono mr-1">{category}</span>
                              <span className="ml-1">{value}</span>
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="italic text-gray-400">No ratings</span>
                      )}
                      {(typeof evalItem.rating_average === 'number' || typeof evalItem.rating_count === 'number') && (
                        <div className="text-xs text-gray-500 mt-1">
                          {typeof evalItem.rating_average === 'number' && (
                            <>Average: <span className="font-semibold">{evalItem.rating_average.toFixed(2)}</span>{' '}</>
                          )}
                          {typeof evalItem.rating_count === 'number' && (
                            <>| Count: <span className="font-semibold">{evalItem.rating_count}</span></>
                          )}
                        </div>
                      )}
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Team Recommendations:</span>{' '}
                      {evalItem.team_recommendations && evalItem.team_recommendations.length > 0
                        ? evalItem.team_recommendations.map((team, idx) => (
                            <span
                              key={team}
                              className="inline-block bg-green-100 text-green-800 text-xs font-semibold rounded px-2 py-1 mr-2 border border-green-200"
                            >
                              {team}
                            </span>
                          ))
                        : <span className="italic text-gray-400">None</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Team Offers Section */}
        <section className="mt-8">
          <h3 className="text-lg font-bold mb-4 border-b pb-1 text-gray-800">Team Offers</h3>
          {loadingOffers ? (
            <div className="py-6 text-center text-gray-500">Loading team offers...</div>
          ) : offersError ? (
            <div className="py-6 text-center text-red-500">{offersError}</div>
          ) : teamOffers.length === 0 ? (
            <div className="py-6 text-center text-gray-400">No team offers found for this player.</div>
          ) : (
            <div className="flex flex-col gap-4">
              {/* Current team offer first, if exists */}
              {teamOffers.filter(offer => offer.team_id === currentTeamId).map(offer => (
                <div key={offer.id} className="rounded-xl border-2 border-indigo-600 bg-indigo-50 px-5 py-3">
                  <div className="flex flex-col md:flex-row md:items-center md:gap-4">
                    <span className="font-semibold text-indigo-800 text-base">
                      {offer.teams?.name || offer.team_id} (Current Team)
                    </span>
                    <span className="ml-2 text-xs text-gray-500">{offer.updated_at?.slice(0, 16).replace('T', ' ')}</span>
                  </div>
                  <div className="mt-1 text-sm">
                    <span className="font-medium text-gray-700">Status:</span> <span className="font-mono px-2 py-1 rounded bg-indigo-100 text-indigo-800 border border-indigo-200">{offer.status}</span>
                  </div>
                  {offer.teams?.description && (
                    <div className="mt-1 text-xs text-gray-600 italic">{offer.teams.description}</div>
                  )}
                </div>
              ))}
              {/* Other teams' offers */}
              {teamOffers.filter(offer => offer.team_id !== currentTeamId).map(offer => (
                <div key={offer.id} className="rounded-xl border bg-white shadow px-5 py-3">
                  <div className="flex flex-col md:flex-row md:items-center md:gap-4">
                    <span className="font-semibold text-gray-800 text-base">
                      {offer.teams?.name || offer.team_id}
                    </span>
                    <span className="ml-2 text-xs text-gray-500">{offer.updated_at?.slice(0, 16).replace('T', ' ')}</span>
                  </div>
                  <div className="mt-1 text-sm">
                    <span className="font-medium text-gray-700">Status:</span> <span className="font-mono px-2 py-1 rounded bg-gray-100 text-gray-800 border border-gray-200">{offer.status}</span>
                  </div>
                  {offer.teams?.description && (
                    <div className="mt-1 text-xs text-gray-600 italic">{offer.teams.description}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button type="button" onClick={onSave}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PlayerDialog;
